<template>
    <section class="member-page">
        <div class="member-page__wrapper">
            <div class="member-page__container">
                <!-- Top Bar: Header & Search -->
                <div class="row align-items-center">
                    <div class="col-sm-auto">
                        <h2 class="member-page__title">
                            <button type="button" class="btn back_btn" @click="$router.go(-1)">
                                <i class="fa fa-chevron-left" aria-hidden="true"></i>
                            </button>Members
                        </h2>
                    </div>
                    <div class="col-sm pt-2 pt-sm-0 member-search">
                        <div class="row">
                            <div class="col">
                                <div class="member-search__box ml-auto">
                                    <label class="member-search__label"
                                        :class="{ 'member-search__label--expanded': isSearchOpen }">
                                        <i class="fa fa-search member-search__icon" @click="isSearchOpen = true"></i>
                                        <form @submit.prevent="executeMemberSearch" class="member-search__form">
                                            <input type="text" placeholder="Search" v-model.trim="search_term"
                                                name="search_term" class="form-control member-search__input"
                                                @focus="isSearchOpen = true" @blur="onSearchBlur"
                                                @input="debounceSearch" autocomplete="off" />
                                        </form>
                                    </label>
                                </div>
                            </div>
                            <div class="col-auto pl-0">
                                <router-link :to="{ name: 'membersConnectedUsers' }"
                                    class="btn btn-primary member-btn-connections">
                                    My Connections
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Filter Slider -->
                <div class="member-filters" v-if="totalCountOfFilters.length > 0">
                    <carousel :mouseDrag="sliderTouchDrag" :touchDrag="sliderTouchDrag" :autoplay="false" :loop="false"
                        :nav="false" :margin="15" :dots="false" :items="4"
                        :responsive="{ 1900: { items: 6 }, 1600: { items: 4 }, 1025: { items: 4 }, 560: { items: 2 }, 320: { items: 1 } }">
                        <template slot="prev">
                            <span class="member-filters__arrow member-filters__arrow--prev">
                                <i class="fa fa-angle-left" aria-hidden="true"></i>
                            </span>
                        </template>
                        <div class="carousel_item animation_zoom_out" v-for="filterItem in totalCountOfFilters"
                            :key="filterItem.id">
                            <div class="member-filters__card"
                                :class="{ 'member-filters__card--active': disabledFilters === filterItem.filter_key }"
                                @click="filteredMemberList(filterItem.filter_key, filterItem.count)"
                                :id="'filtered' + filterItem.filter_key">
                                <div class="member-filters__icon">
                                    <span class="member-filters__img-box">
                                        <img :src="filterItem.image" class="img-fluid" :alt="filterItem.display_name" />
                                    </span>
                                </div>
                                <div class="member-filters__meta">
                                    <span class="member-filters__count">{{ filterItem.count || 0 }}</span>
                                    <span class="member-filters__name">
                                        <div v-html="filterItem.display_name" class="member-filters__name-html"></div>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <template slot="next">
                            <span class="member-filters__arrow member-filters__arrow--next">
                                <i class="fa fa-angle-right" aria-hidden="true"></i>
                            </span>
                        </template>
                    </carousel>
                </div>

                <!-- Member Directory Table -->
                <div class="member-table-wrapper pt-1">
                    <table class="table member-table">
                        <thead>
                            <tr>
                                <th width="20%" class="member-table__th member-table__th--name">Name</th>
                                <th width="18%" class="member-table__th member-table__th--country text-center">Country
                                </th>
                                <th width="15%" class="member-table__th member-table__th--city">City</th>
                                <th width="15%" class="member-table__th member-table__th--org">Hospital <br />
                                    Organization</th>
                                <th width="15%" class="member-table__th member-table__th--dept text-center">Department
                                </th>
                                <th width="6%" class="member-table__th member-table__th--status">Status</th>
                            </tr>
                            <tr class="member-table__empty-row">
                                <th colspan="6"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- Not Found Message Row -->
                            <tr v-if="!memberAllUserResults.length && !userLoading && !mFilterLoading && search_term.length >= 3"
                                class="member-table__row member-table__row--empty">
                                <td colspan="6" class="text-center member-table__cell member-table__cell--empty">
                                    <div class="member-empty-state">
                                        <!-- <i class="fa fa-user-times member-empty-state__icon" aria-hidden="true"></i> -->
                                        <h4 class="member-empty-state__title">No Members Found</h4>
                                        <p class="member-empty-state__desc">
                                            We couldn't find any members matching "<strong>{{ search_term }}</strong>".
                                        </p>
                                        <button type="button" class="btn btn-sm member-btn member-btn--reset" @click="clearSearch">
                                            Clear Search
                                        </button>
                                    </div>
                                </td>
                            </tr>

                            <!-- Member Results Rows -->
                            <tr v-for="member in memberAllUserResults" :key="member.id" class="member-table__row">
                                <td class="member-table__cell member-table__cell--info">
                                    <span class="member-avatar">
                                        <a href="javascript:void(0)" @click="openMemberInfoModal(member)">
                                            <img :src="member.profile && member.profile.thumbnail ? member.profile.thumbnail : defaultUserAvatar"
                                                alt="Avatar" class="member-avatar__image" />
                                        </a>
                                        <i class="member-status-indicator"
                                            :class="{ 'member-status-indicator--online': member.is_online }"></i>
                                    </span>
                                    <span class="member-info">
                                        <span class="member-info__name">
                                            <a href="javascript:void(0)" @click="openMemberInfoModal(member)">
                                                {{ member.salutation }} {{ member.first_name }} {{ member.last_name }}
                                            </a>
                                        </span>
                                        <span class="member-info__actions">
                                            <template v-if="isGuestUser">
                                                <button
                                                    class="btn member-btn member-btn--not-connected">Connect</button>
                                            </template>
                                            <template v-else>
                                                <button v-if="member.already_requested"
                                                    class="btn member-btn member-btn--pending"
                                                    @click="openApproveRequestModal(member.room_id)">
                                                    Approve Connection
                                                </button>
                                                <label v-else-if="member.connection_pending"
                                                    class="badge member-badge member-badge--pending">
                                                    Pending
                                                </label>
                                                <label v-else-if="member.connected"
                                                    class="badge member-badge member-badge--connected">
                                                    Connected
                                                </label>
                                                <button v-else class="btn member-btn member-btn--not-connected"
                                                    @click="openNotConnectedModal(member)">
                                                    Connect
                                                </button>
                                            </template>
                                        </span>
                                    </span>
                                </td>
                                <td class="text-center member-table__cell member-table__cell--country">
                                    <img :src="getCountryFlag(member.profile ? member.profile.country : null)"
                                        class="member-country-flag" alt="flag" />
                                </td>
                                <td class="member-table__cell member-table__cell--city">
                                    <span>{{ (member.profile && member.profile.city) || '-' }}</span>
                                </td>
                                <td class="member-table__cell member-table__cell--org">
                                    <span class="member-org-name">{{ (member.profile && member.profile.area_of_work) ||
                                        '-' }}</span>
                                </td>
                                <td class="text-center member-table__cell member-table__cell--dept">
                                    <span class="member-dept-name">{{ (member.profile && member.profile.department) ||
                                        '-' }}</span>
                                </td>
                                <td class="member-table__cell member-table__cell--status">
                                    <label class="badge member-privacy-badge"
                                        :class="(member.profile && member.profile.is_public) ? 'member-privacy-badge--public' : 'member-privacy-badge--private'">
                                        {{ (member.profile && member.profile.is_public) ? 'Public' : 'Private' }}
                                    </label>
                                </td>
                            </tr>
                            <tr v-if="nextPageUrl" class="member-table__row">
                                <td colspan="6" class="text-center pb-0 member-table__cell">
                                    <button class="btn member-btn-load-more" @click="loadMoreData">
                                        Load More
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Loaders -->
                <div class="member-loader member-loader--more" v-if="userLoading">
                    <div class="spinner spinner-1"></div>
                </div>
                <div class="member-loader member-loader--filter" v-if="mFilterLoading">
                    <div class="spinner spinner-1"></div>
                </div>
            </div>

            <!-- Modals Section in Parent -->
            <not-connected-modal :visible="notConnectedUserPopup" @confirm="proceedToChatRequestModal"
                @close="notConnectedUserPopup = false" />

            <contact-info-modal :visible="connectedUserDetailsPopup" :details="connectedUserDetails"
                @close="connectedUserDetailsPopup = false" />

            <send-connection-request-modal :visible="sendReqModal" :recipient="chatRequestDetails"
                :submitted="submitted" @submit="sendChatRequest" @close="sendReqModal = false" />

            <request-apporve-popup v-if="requestApporvePopupState"
                :requstedUserID="requstedUserId"></request-apporve-popup>

            <member-user-info-popup :memUsrInfo="memUserInfo" v-if="memUsrInfoPopupState"></member-user-info-popup>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import { bus } from '@/main';
import carousel from 'vue-owl-carousel';
import countries from '@/services/country.json';
import { mapGetters } from 'vuex';
import memberUserInfoPopup from '@/components/private/user/member-user-detail-popup';
import root from '@/_helpers/roots';
import toast from '@/services/toast';
import userRequestApporvePopup from '@/components/private/user/user-request-apporve-popup';
import NotConnectedModal from '@/components/private/user/NotConnectedModal.vue';
import ContactInfoModal from '@/components/private/user/ContactInfoModal.vue';
import SendConnectionRequestModal from '@/components/private/user/SendConnectionRequestModal.vue';
import defaultAvatar from '@/assets/images/user2.png';

const memberFilteredUrl = root.memberFitersAllUserListsUrl.link;

export default {
    name: 'member-all-user-list',
    components: {
        carousel,
        NotConnectedModal,
        ContactInfoModal,
        SendConnectionRequestModal,
        'request-apporve-popup': userRequestApporvePopup,
        'member-user-info-popup': memberUserInfoPopup
    },
    data() {
        return {
            defaultUserAvatar: defaultAvatar,
            memberAllUserResults: [],
            nextPageUrl: '',
            userLoading: false,
            mFilterLoading: false,
            submitted: false,
            search_term: '',
            isSearchOpen: false,
            sliderTouchDrag: false,
            disabledFilters: '',

            countryMap: {},

            notConnectedUserPopup: false,
            notConnectedUserDetails: null,
            connectedUserDetailsPopup: false,
            connectedUserDetails: null,
            sendReqModal: false,
            chatRequestDetails: null,
            message: '',

            requestApporvePopupState: false,
            requstedUserId: '',
            memUsrInfoPopupState: false,
            memUserInfo: null,

            searchDebounceTimer: null,
            selfUserId: ''
        };
    },
    computed: {
        ...mapGetters([
            'memberAllUsersGetters',
            'selfUserInfoGetters',
            'getMemberFilterCountGetters'
        ]),
        totalCountOfFilters() {
            return (this.getMemberFilterCountGetters && this.getMemberFilterCountGetters.data) || [];
        },
        currentUser() {
            return (this.selfUserInfoGetters && this.selfUserInfoGetters.data) || null;
        },
        isGuestUser() {
            return !!(this.currentUser && this.currentUser.guest);
        },
        isAnyModalOpen() {
            return (
                this.notConnectedUserPopup ||
                this.connectedUserDetailsPopup ||
                this.sendReqModal ||
                this.requestApporvePopupState ||
                this.memUsrInfoPopupState
            );
        }
    },
    watch: {
        memberAllUsersGetters: {
            handler(newVal) {
                if (newVal && newVal.data) {
                    this.memberAllUserResults = newVal.data.results || [];
                    this.nextPageUrl = newVal.data.next || '';
                }
            },
            immediate: true
        },
        isAnyModalOpen(isOpen) {
            if (isOpen) {
                document.body.classList.add('body_right_scroll_remove');
            } else {
                document.body.classList.remove('body_right_scroll_remove');
            }
        }
    },
    created() {
        this.initCountryMap();
        window.addEventListener('resize', this.onResize, { passive: true });
        this.onResize();
    },
    mounted() {
        this.selfUserId = localStorage.getItem('LoginedUserID');
        this.bindBusEvents();
    },
    beforeRouteLeave(to, from, next) {
        document.body.classList.remove('body_right_scroll_remove');
        next();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
        document.body.classList.remove('body_right_scroll_remove');
        this.unbindBusEvents();
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
    },
    methods: {
        initCountryMap() {
            const map = {};
            Object.keys(countries).forEach(code => {
                map[code.toLowerCase()] = code.toLowerCase();
                map[countries[code].toLowerCase()] = code.toLowerCase();
            });
            this.countryMap = map;
        },
        bindBusEvents() {
            this._userUpdateStatusHandler = (data) => {
                const user = this.memberAllUserResults.find(item => item.id === data.userID);
                if (user) {
                    this.$set(user, 'is_online', data.is_online);
                }
            };
            this._approvePopupHandler = (data) => {
                this.requestApporvePopupState = data;
            };
            this._memInfoPopupHandler = (data) => {
                this.memUsrInfoPopupState = data;
            };
            this._memKeywordHandler = (data) => {
                if (data === 'Connect') {
                    this.openNotConnectedModal(this.memUserInfo);
                } else if (data === 'Approve_Connection') {
                    this.openApproveRequestModal(this.memUserInfo.room_id);
                }
            };

            bus.$on('userUpdateStatus', this._userUpdateStatusHandler);
            bus.$on('requestApporvePopupStateBus', this._approvePopupHandler);
            bus.$on('memUsrInfoPopupStateBus', this._memInfoPopupHandler);
            bus.$on('memUsrInfoPopupKeywordBus', this._memKeywordHandler);
        },
        unbindBusEvents() {
            bus.$off('userUpdateStatus', this._userUpdateStatusHandler);
            bus.$off('requestApporvePopupStateBus', this._approvePopupHandler);
            bus.$off('memUsrInfoPopupStateBus', this._memInfoPopupHandler);
            bus.$off('memUsrInfoPopupKeywordBus', this._memKeywordHandler);
        },
        onResize() {
            this.sliderTouchDrag = window.innerWidth < 769;
        },
        onSearchBlur() {
            if (!this.search_term) {
                this.isSearchOpen = false;
            }
        },
        clearSearch() {
            this.search_term = '';
            this.isSearchOpen = false;
            this.fetchDefaultMemberList();
        },
        debounceSearch() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.executeMemberSearch();
            }, 350);
        },
        async executeMemberSearch() {
            const term = this.search_term;
            if (term.length >= 3) {
                this.userLoading = true;
                try {
                    const res = await this.$store.dispatch('memberUserSearchFun', { search_term: term });
                    const data = res.data && res.data.data ? res.data.data : { results: [], next: '' };
                    this.memberAllUserResults = data.results || [];
                    this.nextPageUrl = data.next || '';
                } catch (err) {
                    console.error(err);
                    this.memberAllUserResults = [];
                } finally {
                    this.userLoading = false;
                }
            } else if (term.length === 0) {
                this.fetchDefaultMemberList();
            }
        },
        async fetchDefaultMemberList() {
            this.userLoading = true;
            try {
                const response = await axios.get(memberFilteredUrl);
                const data = response.data.data;
                this.memberAllUserResults = data.results || [];
                this.nextPageUrl = data.next || '';
            } catch (err) {
                console.error(err);
            } finally {
                this.userLoading = false;
            }
        },
        async filteredMemberList(filterKey, memberCount) {
            if (!memberCount || memberCount <= 0) return;

            const isSameFilter = this.disabledFilters === filterKey;
            const targetUrl = isSameFilter ? memberFilteredUrl : `${memberFilteredUrl}?filter_type=${filterKey}`;

            this.mFilterLoading = true;
            try {
                const response = await axios.get(targetUrl);
                const data = response.data.data;
                this.memberAllUserResults = data.results || [];
                this.nextPageUrl = data.next || '';
                this.disabledFilters = isSameFilter ? '' : filterKey;
            } catch (err) {
                console.error(err);
            } finally {
                this.mFilterLoading = false;
            }
        },
        async loadMoreData() {
            if (!this.nextPageUrl || this.userLoading) return;

            this.userLoading = true;
            try {
                const response = await axios.get(this.nextPageUrl);
                const data = response.data.data;
                this.nextPageUrl = data.next || '';
                if (data.results && data.results.length) {
                    this.memberAllUserResults.push(...data.results);
                }
            } catch (err) {
                console.error(err);
            } finally {
                this.userLoading = false;
            }
        },
        getCountryFlag(countryCode) {
            const code = (countryCode || 'IN').toUpperCase();
            try {
                return require(`@/assets/images/countries/${code}.svg`);
            } catch (e) {
                return require('@/assets/images/countries/IN.svg');
            }
        },
        openMemberInfoModal(member) {
            this.memUserInfo = member;
            this.memUsrInfoPopupState = true;
        },
        openApproveRequestModal(roomId) {
            this.requstedUserId = roomId;
            this.requestApporvePopupState = true;
        },
        openNotConnectedModal(member) {
            this.notConnectedUserDetails = member;
            this.notConnectedUserPopup = true;
        },
        proceedToChatRequestModal() {
            this.chatRequestDetails = this.notConnectedUserDetails;
            this.notConnectedUserPopup = false;
            this.sendReqModal = true;
        },
        async sendChatRequest(messageText) {
            this.submitted = true;
            const payload = {
                from_user: this.currentUser ? this.currentUser.id : this.selfUserId,
                to_user: this.chatRequestDetails.id,
                message: messageText
            };

            try {
                const res = await this.$store.dispatch('SendChatConnectionRequestFun', payload);
                toast.success(res.data.user_msg || res.data.msg);
                this.$store.dispatch('memberAllUsersListFun');
                this.$store.dispatch('fetchPinnedChatRooms').then(() => {
                    this.$store.dispatch('fetchNormalChatRooms');
                });
            } catch (error) {
                const errData = error.response ? error.response.data : error.data;
                toast.error((errData && (errData.user_msg || errData.msg)) || 'Failed to send request');
            } finally {
                this.submitted = false;
                this.sendReqModal = false;
                this.chatRequestDetails = null;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.member-page {
    background: rgba(0, 0, 0, .6);
    border-radius: 3px;
    padding: 15px;
    min-height: calc(100dvh - 80px);
    position: relative;
    width: 100%;

    &__wrapper {
        color: #ffffff;
        font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif;
        letter-spacing: 1px;
    }

    &__title {
        color: #ffffff;
        font-size: 24px;
        text-transform: uppercase;
        font-weight: bold;
        margin: 0px;
    }
}

/* Search Bar */
.member-search {
    &__box {
        position: relative;
        max-width: 340px;
        width: 100%;
        text-align: right;

        @media only screen and (max-width: 767px) {
            max-width: 100%;
        }
    }

    &__label {
        background: linear-gradient(-30deg, rgba(37, 44, 84, 0.6) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(38, 38, 38, 0.6) 50%, rgba(44, 44, 84, 0.6) 100%);
        border: 1px solid rgba(93, 93, 93, 0.4);
        display: inline-flex;
        width: 40px;
        height: 36px;
        border-radius: 3px;
        overflow: hidden;
        vertical-align: middle;
        margin-bottom: 0;
        transition: all 0.5s ease;

        &:hover,
        &--expanded {
            width: 100%;
            border-color: #ffd400;

            .member-search__icon,
            .member-search__input {
                color: #ffd400 !important;
            }
        }
    }

    &__icon {
        font-size: 18px;
        cursor: pointer;
        color: #ffffff;
        line-height: 36px;
        margin-left: 10px;
    }

    &__form {
        width: 100%;
    }

    &__input {
        background: none;
        font-size: 18px;
        color: #ffffff;
        font-family: 'MyriadProRegular', 'Lato', sans-serif;
        border: none;
        line-height: 36px;
        padding: 0 15px;
        height: auto !important;
        box-shadow: none;

        &::-webkit-input-placeholder {
            color: #ffffff;
            opacity: 0.8;
        }

        &::-moz-placeholder {
            color: #ffffff;
            opacity: 0.8;
        }

        &:-ms-input-placeholder {
            color: #ffffff;
            opacity: 0.8;
        }
    }
}

.member-btn-connections {
    min-width: 95px;
    background: #b41818 !important;
    color: #fffcc4 !important;
    border: none;
    border-radius: 3px;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    font-size: 17px;
    line-height: 35px;
    padding: 0 12px;
    box-shadow: none !important;

    &:hover {
        background: #87cefa !important;
        color: #000000 !important;
    }

    @media only screen and (max-width: 767px) {
        font-size: 16px;
        padding: 0 10px;
    }
}

/* Category Filter Slider */
.member-filters {
    padding: 12px 0;
    position: relative;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;

    &__card {
        background: linear-gradient(-30deg, rgba(37, 44, 84, 0.6) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(38, 38, 38, 0.6) 50%, rgba(44, 44, 84, 0.6) 100%);
        padding: 0 0 35px 18px;
        border: 1px solid rgba(93, 93, 93, 0.4);
        border-radius: 5px;
        display: flex;
        align-items: flex-end;
        cursor: pointer;
        color: #fff;
        height: 135px;

        &:hover,
        &--active {
            background: #87cefa;
            color: #000000;

            .member-filters__icon span {
                background: rgba(0, 0, 0, 0.1);

                .img-fluid {
                    filter: invert(100%) brightness(0%);
                }
            }
        }
    }

    &__icon span {
        width: 60px;
        display: inline-block;
        height: 60px;
        background: hsla(0, 0%, 100%, 0.1);
        border-radius: 50%;
        text-align: center;
        line-height: 60px;

        .img-fluid {
            width: 40px !important;
            height: 40px !important;
            display: inline-block !important;
        }
    }

    &__meta {
        padding-left: 15px;
    }

    &__count {
        display: block;
        font-size: 22px;
        font-weight: 600;
    }

    &__name {
        font-size: 20px;
        text-transform: capitalize;
        line-height: 1;
    }

    &__arrow {
        position: absolute;
        z-index: 99;
        top: 50%;
        left: -21px;
        transform: translateY(-50%);

        .fa {
            cursor: pointer;
            font-size: 42px;
            color: #ffc107;

            &:hover {
                color: #87cefa;
            }
        }

        &--next {
            right: -21px;
            left: auto;
        }

        @media only screen and (max-width: 767px) {
            left: -15px;

            &--next {
                right: -15px;
                left: auto;
            }
        }
    }
}

/* Directory Table */
.member-table-wrapper {
    min-height: 55vh;
}

.member-table {
    margin-bottom: 0;
    letter-spacing: 0.5px;
    color: #ffffff;
    border-collapse: separate;
    border-spacing: 0 3px;

    &__th {
        border-bottom: 0;
        font-size: 19px;
        padding: 10px 8px;
        font-family: 'MyriadProRegular', 'Lato', sans-serif;
        text-transform: uppercase;
        border-top: 0;
        background: rgba(228, 233, 237, 0.1);
        color: #87cefa;
    }

    &__empty-row th {
        padding: 0 !important;
        background: none !important;
        border: none !important;
    }

    &__cell {
        color: #ffffff;
        vertical-align: middle;
        padding: 10px 8px;
        font-size: 20px;
        font-family: 'MyriadProRegular', 'Lato', sans-serif;
        height: 120px;
        border-top: 0;
        background: rgba(228, 233, 237, 0.1);
    }
}

/* Empty State / Not Found */
.member-table__row--empty {
    background: transparent;
}

.member-table__cell--empty {
    height: auto !important;
    padding: 40px 15px !important;
    background: rgba(228, 233, 237, 0.05) !important;
}

.member-empty-state {
    text-align: center;
    color: #ffffff;

    &__icon {
        font-size: 48px;
        color: #ffd400;
        margin-bottom: 15px;
        display: inline-block;
    }

    &__title {
        font-size: 22px;
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 8px;
    }

    &__desc {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.7);
        margin-bottom: 18px;

        strong {
            color: #87cefa;
        }
    }
}

.member-btn--reset {
    background: #ffd400 !important;
    color: #000000 !important;
    font-size: 15px !important;
    padding: 6px 18px !important;
    border-radius: 4px;
    font-weight: 600;

    &:hover {
        background: #87cefa !important;
        color: #000000 !important;
    }
}

/* User Card in Table */
.member-avatar {
    display: inline-block;
    vertical-align: middle;
    position: relative;

    &__image {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        cursor: pointer;
        object-fit: cover;
    }
}

.member-status-indicator {
    position: absolute;
    right: 4px;
    bottom: 2px;
    border: 1px solid #9e9e9e;
    border-radius: 50%;
    width: 12px;
    height: 12px;
    background: #9e9e9e;

    &--online {
        background-color: #4caf50;
        border-color: #4caf50;
    }
}

.member-info {
    display: inline-block;
    vertical-align: middle;
    width: calc(100% - 70px);
    margin-left: 10px;

    &__name {
        line-height: 1;
        font-size: 20px;
        color: #fff;
        display: block;
        cursor: pointer;
        text-transform: capitalize;

        a {
            color: #ffffff;
            text-decoration: none;

            &:hover {
                color: #ffd400;
            }
        }
    }

    &__actions {
        margin-top: 15px;
        display: block;
    }
}

.member-btn {
    padding: 7px 15px;
    font-size: 17px !important;
    border-radius: 70px;
    line-height: 1;
    text-transform: capitalize;
    border: none;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    margin-right: 5px;

    &--not-connected {
        background: #b41818;
        color: #fffcc4;
    }

    &--pending {
        background: #ffd400;
        color: #000000;
    }

    &:hover {
        background: #87cefa;
        color: #22292f;
    }
}

.member-badge {
    font-weight: normal;
    font-size: 16px;
    padding: 7px 15px;
    border-radius: 70px;
    cursor: default;

    &--pending {
        background: #ffd400;
        color: #000000;
    }

    &--connected {
        background: #28a745;
        color: #ffffff;
    }
}

.member-country-flag {
    width: 50px;
    display: inline-block;
    vertical-align: middle;
    margin-right: 5px;
    object-fit: cover;
}

.member-org-name {
    display: block;
    max-width: 180px;
}

.member-privacy-badge {
    font-size: 16px;
    font-weight: normal;

    &--public {
        background-color: #28a745;
    }

    &--private {
        background-color: #6c757d;
    }
}

.member-btn-load-more {
    color: #fff !important;
    background: rgba(255, 255, 255, 0.1);
    padding: 5px;
    min-width: 110px;
    font-size: 16px;
    border-radius: 3px;
    border: 0 !important;
    margin: 15px 0;

    &:hover {
        color: #000 !important;
        background: #87cefa;
    }
}

/* Loaders */
.member-loader {
    display: flex;
    justify-content: center;
    position: absolute;
    inset: 0;
    z-index: 1;
    background: rgba(0, 0, 0, 0.72);

    &--filter {
        align-items: flex-start;
        padding-top: 200px;
    }

    &--more {
        align-items: flex-end;
        padding-bottom: 150px;
    }
}

/* Mobile Responsive */
@media only screen and (max-width: 769px) {
    .member-table {
        display: block;

        thead {
            display: none;
        }

        tbody,
        &__row,
        &__cell {
            display: block;
        }

        &__row {
            background: rgba(228, 233, 237, 0.1);
            margin-bottom: 10px;
            padding: 10px;

            &--empty {
                background: none;
                padding: 0;
            }
        }

        &__cell {
            height: auto !important;
            background: none !important;
            padding: 5px 0;
            text-align: left !important;

            &--empty {
                text-align: center !important;
                background: rgba(228, 233, 237, 0.05) !important;
                padding: 30px 10px !important;
            }

            &--country,
            &--city,
            &--status {
                display: inline-block;
            }
        }
    }
}
</style>

<style lang="scss">
.member-filters__name-html p {
    margin: 0 !important;
}
</style>